import { useCallback, useEffect, useRef, useState } from "react";
import { EventModel } from "../models/EventModel";

export type EventPage = { events: EventModel[]; nextCursor: string | null; hasMore: boolean; asOf?: string };
type CacheEntry = EventPage & { ts: number; scrollY?: number };
type State = EventPage & { key: string; loading: boolean; loadingMore: boolean; error: string | null; restoreScrollY?: number };
const eventsCache = new Map<string, CacheEntry>();
const emptyPage: EventPage = { events: [], nextCursor: null, hasMore: false };

export function usePaginatedEvents(key: string, options?: { maxAgeMs?: number; enabled?: boolean; initialPage?: EventPage | null; restoreScroll?: boolean }) {
    const maxAgeMs = options?.maxAgeMs ?? 60 * 60 * 1000;
    const enabled = options?.enabled ?? true;
    const initial = useRef({ key, page: options?.initialPage });
    const [state, setState] = useState<State>({ ...emptyPage, key: '', loading: true, loadingMore: false, error: null });
    const requestRef = useRef<(() => void) | null>(null);
    const restorationKey = useRef<string | null>();

    useEffect(() => {
        if (!enabled) return;
        if (restorationKey.current === undefined) restorationKey.current = key;
        else if (restorationKey.current !== key) restorationKey.current = null;
        let active = true;
        let busy = false;
        let controller: AbortController | null = null;
        const cached = eventsCache.get(key);
        let entry: CacheEntry | null = cached && Date.now() - cached.ts < maxAgeMs ? cached
            : initial.current.key === key && initial.current.page ? { ...initial.current.page, ts: Date.now() } : null;
        if (entry) eventsCache.set(key, entry);
        const publish = (loading: boolean, loadingMore: boolean, error: string | null) => {
            if (active) setState({ ...(entry ?? emptyPage), key, loading, loadingMore, error,
                restoreScrollY: restorationKey.current === key ? entry?.scrollY : undefined });
        };
        const request = async () => {
            if (!active || busy || (entry && !entry.hasMore)) return;
            const url = new URL(key, window.location.origin);
            const params = url.searchParams;
            busy = true;
            controller = new AbortController();
            publish(!entry, Boolean(entry), null);
            try {
                if (entry?.nextCursor) params.set('cursor', entry.nextCursor);
                if (entry?.asOf) params.set('asOf', entry.asOf);
                const response = await fetch(`${url.pathname}?${params}`, { signal: controller.signal });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const page: EventPage = await response.json();
                if (!active) return;
                if (!Array.isArray(page.events) || typeof page.hasMore !== 'boolean'
                    || (page.hasMore && (!page.nextCursor || page.nextCursor === entry?.nextCursor))) {
                    throw new Error('Invalid event page');
                }
                entry = { ...page, events: [...(entry?.events ?? []), ...page.events], ts: entry?.ts ?? Date.now(), scrollY: entry?.scrollY };
                // Keep completed batches together for back navigation, without an unbounded cache.
                eventsCache.delete(key);
                eventsCache.set(key, entry);
                if (eventsCache.size > 20) eventsCache.delete(eventsCache.keys().next().value!);
                publish(false, false, null);
            } catch (error) {
                if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
                    publish(false, false, entry ? 'Impossible de charger la suite des événements.' : 'Impossible de charger les événements.');
                }
            } finally {
                busy = false;
            }
        };
        requestRef.current = () => { void request(); };
        if (entry) publish(false, false, null);
        else void request();
        return () => {
            active = false;
            controller?.abort();
            requestRef.current = null;
        };
    }, [key, maxAgeMs, enabled]);

    useEffect(() => {
        if (!options?.restoreScroll || state.key !== key || state.loading || state.restoreScrollY === undefined) return;
        const frame = requestAnimationFrame(() => window.scrollTo(0, state.restoreScrollY!));
        return () => cancelAnimationFrame(frame);
    }, [options?.restoreScroll, key, state.key, state.loading, state.restoreScrollY]);

    const loadMore = useCallback(() => requestRef.current?.(), []);
    const rememberScroll = useCallback(() => {
        const entry = eventsCache.get(key);
        if (entry) entry.scrollY = window.scrollY;
    }, [key]);
    const current = state.key === key && enabled ? state : { ...emptyPage, loading: true, loadingMore: false, error: null, restoreScrollY: undefined };
    return { ...current, loadMore, rememberScroll, queryKey: key };
}
